/*
  Every file attached to a policy, in one table.

  Flexibly many, of several kinds — a certificate, a sticker, a receipt, a proposal form, an ID.
  One table, because a document that lives somewhere else is a document nobody finds.

  Three things per row, and the ORDER of them matters. Download first: it is the one that always
  works. Send is offered next and will sometimes fail for reasons nobody here can fix — outside
  WhatsApp's 24-hour window with no approved template, a number not on WhatsApp, Meta simply
  refusing — and when it does, Download is still sitting there. The way through must not depend on
  Meta.

  Superseded rows hide behind a toggle. A reissued certificate that left two rows looking equally
  current would be two documents with nothing to say which the customer is holding.
*/
import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Card, Empty, Input, Modal, Select, Space, Table, Tag, Upload } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import moment from 'moment'
import {
  postRequest,
  deleteRequest,
  insurancePolicyDocumentsUrl,
  insuranceDocumentUrl,
  insuranceDocumentSendUrl,
  errorNotification,
  successNotification
} from '../../config'

const { Option } = Select

const serverSaid = response =>
  response.error?.response?.data?.detail || response.data?.detail

// The server's own list. Kept in step by the API refusing anything not in it, which is why a
// mismatch here is a 400 the agent can act on rather than a document filed as the wrong thing.
const DOC_TYPES = [
  { value: 'certificate', label: 'Certificate' },
  { value: 'sticker', label: 'Sticker' },
  { value: 'receipt', label: 'Receipt' },
  { value: 'proposal', label: 'Proposal form' },
  { value: 'id', label: 'ID document' },
  { value: 'other', label: 'Other' }
]

const PolicyDocuments = ({ policy, onChanged }) => {
  const [adding, setAdding] = useState(false)
  const [busy, setBusy] = useState(false)
  const [file, setFile] = useState(null)
  const [docType, setDocType] = useState('certificate')
  const [label, setLabel] = useState('')
  const [showOlder, setShowOlder] = useState(false)

  const documents = policy.documents || []
  const shown = showOlder ? documents : documents.filter(row => row.isCurrent)

  const upload = async () => {
    if (!file) return
    setBusy(true)
    const payload = new FormData()
    payload.append('file', file)
    payload.append('docType', docType)
    payload.append('label', label)
    const response = await postRequest(
      insurancePolicyDocumentsUrl(policy.id),
      payload,
      'document',
      () => {},
      false // multipart
    )
    setBusy(false)
    if (!response.success) {
      errorNotification(serverSaid(response) || 'That file could not be stored.')
      return
    }
    successNotification('Added.')
    setAdding(false)
    setFile(null)
    setLabel('')
    onChanged(response.data.policy)
  }

  const send = document => {
    Modal.confirm({
      title: `Send this ${document.docTypeLabel.toLowerCase()} on WhatsApp?`,
      content: (
        <Space direction="vertical">
          <span>
            <strong>{document.label || document.docTypeLabel}</strong> goes to{' '}
            <strong>{policy.phone}</strong>.
          </span>
          <span style={{ opacity: 0.65 }}>
            It is sent from our assistant number, not from your own WhatsApp — so it arrives from a
            number the customer may not have been talking to.
          </span>
        </Space>
      ),
      okText: 'Send it',
      onOk: async () => {
        const response = await postRequest(
          insuranceDocumentSendUrl(policy.id, document.id),
          {},
          'document',
          () => {}
        )
        if (!response.success) {
          errorNotification(serverSaid(response) || 'That could not be sent.')
          return
        }
        // A failed send is a 200: it genuinely was attempted and genuinely was recorded. What is
        // left to do is tell the agent to download it and send it themselves.
        if (response.data.result === 'failed') {
          errorNotification(
            response.data.error || 'WhatsApp refused it. Download it and send it yourself.'
          )
        } else {
          successNotification('Sent.')
        }
        onChanged(null)
      }
    })
  }

  const remove = document => {
    Modal.confirm({
      title: 'Remove this document?',
      content: `${document.label || document.docTypeLabel} will be deleted from this policy.`,
      okText: 'Remove it',
      okButtonProps: { danger: true },
      onOk: async () => {
        const response = await deleteRequest(
          insuranceDocumentUrl(policy.id, document.id),
          'document',
          () => {}
        )
        if (!response.success) {
          errorNotification(serverSaid(response) || 'That could not be removed.')
          return
        }
        successNotification('Removed.')
        onChanged(null)
      }
    })
  }

  /* antd cell renderers return JSX, which the display-name rule reads as anonymous components. */
  /* eslint-disable react/display-name */
  const columns = [
    {
      title: 'Document',
      key: 'doc',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Space size={6}>
            <strong>{r.label || r.docTypeLabel}</strong>
            {r.isCurrent ? null : <Tag>superseded</Tag>}
          </Space>
          <span style={{ opacity: 0.65, fontSize: 12 }}>{r.docTypeLabel}</span>
        </Space>
      )
    },
    {
      title: 'Added',
      key: 'added',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <span>{moment(r.createdAt).format('D MMM, HH:mm')}</span>
          <span style={{ opacity: 0.65, fontSize: 12 }}>{r.uploadedBy || '—'}</span>
        </Space>
      )
    },
    {
      title: 'Last sent',
      key: 'sent',
      render: (_, r) =>
        r.lastSend ? (
          <Space direction="vertical" size={0}>
            <Tag color={r.lastSend.status === 'sent' ? 'green' : 'red'}>{r.lastSend.status}</Tag>
            {r.lastSend.error ? (
              <span style={{ opacity: 0.65, fontSize: 12 }}>{r.lastSend.error}</span>
            ) : null}
          </Space>
        ) : (
          <span style={{ opacity: 0.5 }}>never</span>
        )
    },
    {
      title: '',
      key: 'actions',
      render: (_, r) => (
        <Space>
          {/*
            First, and never disabled by anything. This is the way through when WhatsApp will not
            cooperate, which it often will not.
          */}
          <Button size="small" href={r.url} target="_blank" rel="noopener noreferrer">
            Download
          </Button>
          <Button size="small" onClick={() => send(r)}>
            Send
          </Button>
          <Button size="small" danger onClick={() => remove(r)}>
            Remove
          </Button>
        </Space>
      )
    }
  ]
  /* eslint-enable react/display-name */

  const older = documents.length - documents.filter(row => row.isCurrent).length

  return (
    <Card
      size="small"
      title="Documents"
      extra={
        <Space>
          {older > 0 ? (
            <Button size="small" type="link" onClick={() => setShowOlder(!showOlder)}>
              {showOlder ? 'Hide older' : `Show ${older} older`}
            </Button>
          ) : null}
          <Button size="small" type="primary" onClick={() => setAdding(true)}>
            Upload
          </Button>
        </Space>
      }
    >
      <Table
        rowKey="id"
        size="small"
        dataSource={shown}
        columns={columns}
        pagination={false}
        locale={{ emptyText: <Empty description="Nothing attached yet" /> }}
      />

      <Modal
        visible={adding}
        title="Add a document"
        okText="Upload"
        confirmLoading={busy}
        okButtonProps={{ disabled: !file }}
        onCancel={() => {
          setAdding(false)
          setFile(null)
        }}
        onOk={upload}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <label>What is it?</label>
            <Select value={docType} onChange={setDocType} style={{ width: '100%' }}>
              {DOC_TYPES.map(row => (
                <Option key={row.value} value={row.value}>
                  {row.label}
                </Option>
              ))}
            </Select>
            <div style={{ opacity: 0.65, fontSize: 12, marginTop: 4 }}>
              A new certificate supersedes the previous certificate and leaves everything else
              alone.
            </div>
          </div>
          <div>
            <label>Label (optional)</label>
            <Input
              value={label}
              onChange={e => setLabel(e.target.value)}
              placeholder="Star cert (corrected)"
            />
          </div>
          <Upload
            beforeUpload={selected => {
              setFile(selected)
              return false
            }}
            maxCount={1}
            onRemove={() => setFile(null)}
          >
            <Button icon={<UploadOutlined />}>Choose a file</Button>
          </Upload>
          <div style={{ opacity: 0.65, fontSize: 12 }}>PDF or image, up to 10MB.</div>
        </Space>
      </Modal>
    </Card>
  )
}

PolicyDocuments.propTypes = {
  policy: PropTypes.object.isRequired,
  onChanged: PropTypes.func.isRequired
}

export default PolicyDocuments
